import { supabase } from './supabase';

export interface CrmPipeline {
  id: string;
  coach_id: string;
  name: string;
  created_at: string;
}

export interface CrmStage {
  id: string;
  pipeline_id: string;
  name: string;
  position: number;
  color: string;
}

export interface CrmLead {
  id: string;
  coach_id: string;
  pipeline_id: string;
  stage_id: string;
  full_name: string;
  email: string;
  phone: string | null;
  source: string;
  notes: string | null;
  first_message: string | null;
  follow_up_date: string | null;
  user_id: string | null;
  created_at: string;
  updated_at: string;
}

const DEFAULT_STAGES = [
  { name: 'New Lead', position: 0, color: '#3b82f6' },
  { name: 'Contacted', position: 1, color: '#f59e0b' },
  { name: 'Qualified', position: 2, color: '#8b5cf6' },
  { name: 'Proposal', position: 3, color: '#06b6d4' },
  { name: 'Won', position: 4, color: '#10b981' },
  { name: 'Lost', position: 5, color: '#ef4444' },
];

export async function getOrCreatePipeline(coachId: string): Promise<CrmPipeline | null> {
  const { data: existing } = await supabase
    .from('crm_pipelines')
    .select('*')
    .eq('coach_id', coachId)
    .order('created_at')
    .limit(1)
    .maybeSingle();

  if (existing) return existing;

  const { data: pipeline, error } = await supabase
    .from('crm_pipelines')
    .insert({ coach_id: coachId, name: 'Sales Pipeline' })
    .select()
    .single();

  if (error || !pipeline) return null;

  const stages = DEFAULT_STAGES.map(s => ({
    ...s,
    pipeline_id: pipeline.id,
  }));

  await supabase.from('crm_stages').insert(stages);

  return pipeline;
}

export async function getStages(pipelineId: string): Promise<CrmStage[]> {
  const { data } = await supabase
    .from('crm_stages')
    .select('*')
    .eq('pipeline_id', pipelineId)
    .order('position');
  return data || [];
}

export async function getLeads(coachId: string, pipelineId: string): Promise<CrmLead[]> {
  const { data } = await supabase
    .from('crm_leads')
    .select('*')
    .eq('coach_id', coachId)
    .eq('pipeline_id', pipelineId)
    .order('created_at', { ascending: false });
  return (data || []) as CrmLead[];
}

export async function createLead(lead: {
  coach_id: string;
  pipeline_id: string;
  stage_id: string;
  full_name: string;
  email: string;
  phone?: string;
  source?: string;
  notes?: string;
  first_message?: string;
  follow_up_date?: string;
  user_id?: string;
}) {
  return supabase.from('crm_leads').insert(lead).select().single();
}

export async function updateLeadStage(leadId: string, stageId: string) {
  return supabase
    .from('crm_leads')
    .update({ stage_id: stageId, updated_at: new Date().toISOString() })
    .eq('id', leadId);
}

export async function updateLead(leadId: string, data: Partial<CrmLead>) {
  return supabase
    .from('crm_leads')
    .update({ ...data, updated_at: new Date().toISOString() })
    .eq('id', leadId);
}

export async function deleteLead(leadId: string) {
  return supabase.from('crm_leads').delete().eq('id', leadId);
}

export async function addStage(pipelineId: string, name: string, position: number, color: string) {
  return supabase.from('crm_stages').insert({ pipeline_id: pipelineId, name, position, color }).select().single();
}

export async function updateStage(stageId: string, data: { name?: string; position?: number; color?: string }) {
  return supabase.from('crm_stages').update(data).eq('id', stageId);
}

export async function deleteStage(stageId: string) {
  return supabase.from('crm_stages').delete().eq('id', stageId);
}

export async function autoCreateLead(coachId: string, lead: {
  full_name: string;
  email: string;
  source: string;
  first_message?: string;
  user_id?: string;
}) {
  const pipeline = await getOrCreatePipeline(coachId);
  if (!pipeline) return;

  const stages = await getStages(pipeline.id);
  const firstStage = stages[0];
  if (!firstStage) return;

  const { data: existing } = await supabase
    .from('crm_leads')
    .select('id')
    .eq('coach_id', coachId)
    .eq('email', lead.email)
    .maybeSingle();

  if (existing) return;

  await createLead({
    coach_id: coachId,
    pipeline_id: pipeline.id,
    stage_id: firstStage.id,
    full_name: lead.full_name,
    email: lead.email,
    source: lead.source,
    first_message: lead.first_message,
    user_id: lead.user_id,
  });

  const leadBody = {
    coach_id: coachId,
    full_name: lead.full_name,
    email: lead.email,
    source: lead.source,
  };

  // Fire-and-forget pushes — failures must never block the user flow
  supabase.functions.invoke('push-ghl-lead', { body: leadBody }).catch(() => {});
  supabase.functions.invoke('push-hubspot-lead', { body: leadBody }).catch(() => {});
}
